import cn from "classnames";
import { forwardRef } from "react";

import { getBorderRadiusProps } from "@/components/BorderRadius/BorderRadius";
import { Stack } from "@/components/Stack/Stack";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./Alert.module.scss";

export type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * The severity of the alert. This defines the color used.
   * @default info
   */
  type: "info" | "success" | "warning" | "error";
  /**
   * The icon to be displayed.
   */
  icon?: React.ReactNode;
};

/**
 * A component used to present disclaimer information to users. Used within the form sections during the OSB flow.
 *
 * ## Usage
 *
 * ```tsx
 *  <Alert type="info">Hello World</Alert>
 * ```
 */
const Alert = /*#__PURE__*/ (function () {
  const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
    const { children, type = "info", icon, ...rest } = props;

    return (
      <div
        ref={ref}
        aria-label={type}
        role="alert"
        {...mergeProps(rest, getBorderRadiusProps("small"), {
          className: cn(styles.alert, styles[type]),
        })}
      >
        <Stack spacing="3xs" alignItems="center">
          {icon && <div className={styles.iconContainer}>{icon}</div>}
          {children}
        </Stack>
      </div>
    );
  });

  Alert.displayName = "Alert";

  return Alert;
})();

export { Alert };
